import BusinessIcon from '@material-design-icons/svg/filled/business.svg?react';
import CloudDownloadIcon from '@material-design-icons/svg/filled/cloud_download.svg?react';
import GroupIcon from '@material-design-icons/svg/filled/group.svg?react';
import KeyboardArrowDownIcon from '@material-design-icons/svg/filled/keyboard_arrow_down.svg?react';
import PersonIcon from '@material-design-icons/svg/filled/person.svg?react';
import { useStore } from '@tanstack/react-store';
import type { SortingState } from '@tanstack/react-table';
import { isRight } from 'fp-ts/Either';
import { use, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { DataTable } from '../components/common/DataTable';
import { TypedStatusBadge } from '../components/common/StatusBadge';
import {
  ListEmptyState,
  ListHeader,
  ListPager,
  ListShell,
  listColumn,
  RelativeTime,
  RowList,
} from '../components/management/ListShell';
import {
  ListToolbar,
  SegmentedControl,
  SortMenu,
  type SortOption,
  ToolbarSearch,
  ToolbarSelect,
  toolbarControlClassName,
} from '../components/management/ListToolbar';
import { ManagementPageLayout } from '../components/management/ManagementPageLayout';
import { S3Image } from '../components/S3Image';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { TRPCContext } from '../contexts/TRPC';
import { useManagementCrud } from '../hooks/useManagementCrud';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { usePageTitle } from '../hooks/usePageTitle';
import type { QueryLoadContext } from '../hooks/usePaginatedQueryLoader';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { useUqlUrlSync } from '../hooks/useUqlUrlSync';
import { downloadFile } from '../lib/formatters';
import { cn } from '../lib/utils';
import {
  type PublisherDetail,
  type PublishersPageState,
  publishersPageStore,
  resetFilters,
  setLoading,
  setPage,
  setPaginationInfo,
  setPublishersQueryState,
  setPublishers,
  setSorting,
  updateFilter,
  updatePublisherKycKybBypassState,
} from '../stores/publishersPageStore';
import { captureAndReportError } from '../utils/errorReporting';
import {
  buildPublishersUql,
  parsePublishersUql,
} from '../utils/publishersPageUrlState';
import {
  buildPublisherCsv,
  defaultPublisherExportFieldKeys,
  type PublisherExportFieldKey,
  publisherExportFields,
} from '../utils/publisherCsvExport';
import {
  canAccessPublisherPages,
  hasAdminAccess,
  hasSuperadminAccess,
} from '../utils/roleUtils';
import { PublisherReviewRepliesView } from './ReviewRepliesPage';

const PAGE_SIZE = 20;

type PublishersFilters = PublishersPageState['filters'];

const publisherApiSortIds = [
  'created',
  'updated',
  'name',
  'kybStatus',
  'dappCount',
  'type',
] as const;

type PublisherApiSortId = (typeof publisherApiSortIds)[number];

const TYPE_OPTIONS: ReadonlyArray<{
  value: PublishersFilters['type'];
  label: string;
}> = [
  { value: 'all', label: 'All types' },
  { value: 'Organization', label: 'Organizations' },
  { value: 'Individual', label: 'Individuals' },
];

const KYB_OPTIONS: ReadonlyArray<{
  value: PublishersFilters['kybStatus'];
  label: string;
}> = [
  { value: 'all', label: 'KYB: any' },
  { value: 'NotStarted', label: 'Not started' },
  { value: 'InProgress', label: 'In progress' },
  { value: 'UnderReview', label: 'Under review' },
  { value: 'RequiresAdditionalInfo', label: 'Needs info' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
];

const DAPPS_OPTIONS: ReadonlyArray<{
  value: PublishersFilters['hasDapps'];
  label: string;
}> = [
  { value: 'all', label: 'Dapps: any' },
  { value: 'true', label: 'With dapps' },
  { value: 'false', label: 'Without dapps' },
];

const OWNERS_OPTIONS: ReadonlyArray<{ value: 'all' | 'true'; label: string }> =
  [
    { value: 'all', label: 'All members' },
    { value: 'true', label: 'Owners only' },
  ];

const SORT_OPTIONS: ReadonlyArray<SortOption> = [
  { id: 'created', desc: true, label: 'Newest first' },
  { id: 'created', desc: false, label: 'Oldest first' },
  { id: 'updated', desc: true, label: 'Recently updated' },
  { id: 'name', desc: false, label: 'Name A–Z' },
  { id: 'kybStatus', desc: false, label: 'KYB status' },
  { id: 'dappCount', desc: true, label: 'Most dapps' },
];

type PublisherView = 'publishers' | 'publisher-replies';

const VIEW_OPTIONS: ReadonlyArray<{ value: PublisherView; label: string }> = [
  { value: 'publishers', label: 'Publishers' },
  { value: 'publisher-replies', label: 'Publisher replies' },
];

interface PublishersPageLoadContext {
  filters: PublishersFilters;
  sorting: SortingState;
  pagination: { currentPage: number; totalPages: number; total: number };
}

function getPublisherApiSorting(sorting: SortingState) {
  const apiSorting = sorting
    .filter((sort): sort is SortingState[number] & { id: PublisherApiSortId } =>
      publisherApiSortIds.includes(sort.id as PublisherApiSortId),
    )
    .map((sort) => ({ id: sort.id, desc: sort.desc }));

  return apiSorting.length > 0 ? apiSorting : undefined;
}

/** Publisher directory: find an account, check its KYB standing, jump in. */
export function PublishersPage() {
  const { authState, canRender } = useRequireAuth({
    requireRole: 'publisherStaff',
  });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const trpc = use(TRPCContext);
  const isNarrow = useMediaQuery('(max-width: 767px)');

  const publishers = useStore(publishersPageStore, (state) => state.publishers);
  const requestedView = searchParams.get('view');
  const [updatingKycBypassPublisherKeys, setUpdatingKycBypassPublisherKeys] =
    useState<Set<string>>(new Set());
  const [selectedExportFields, setSelectedExportFields] = useState<
    PublisherExportFieldKey[]
  >(() => [...defaultPublisherExportFieldKeys]);
  const [isExportingPublishers, setIsExportingPublishers] = useState(false);
  const canManagePublishers =
    authState.type === 'Authenticated' &&
    hasAdminAccess(authState.user.role, authState.user.email);
  const canAccessPublisherReplies =
    authState.type === 'Authenticated' &&
    hasSuperadminAccess(authState.user.role, authState.user.email);
  const activeView: PublisherView =
    requestedView === 'publisher-replies' && canAccessPublisherReplies
      ? 'publisher-replies'
      : 'publishers';

  usePageTitle(
    activeView === 'publisher-replies'
      ? 'Publisher Replies | Publishing Portal'
      : 'Publishers | Publishing Portal',
  );

  const updateActiveView = (view: PublisherView) => {
    const nextParams = new URLSearchParams(searchParams);
    if (view === 'publisher-replies') {
      nextParams.set('view', 'publisher-replies');
    } else {
      nextParams.delete('view');
    }
    setSearchParams(nextParams, { replace: true });
  };

  const loadPublishers = async ({
    isCurrentRequest,
    filters,
    sorting,
    pagination,
  }: QueryLoadContext & PublishersPageLoadContext) => {
    if (!trpc?.client) return;

    try {
      const result = await trpc.client.listAllPublishers.query({
        filters: {
          type: filters.type,
          kybStatus: filters.kybStatus,
          hasDapps: filters.hasDapps,
          ownersOnly:
            canManagePublishers && filters.ownersOnly ? true : undefined,
          search: filters.search || undefined,
        },
        pagination: { page: pagination.currentPage, limit: PAGE_SIZE },
        sorting: getPublisherApiSorting(sorting),
      });

      if (!isCurrentRequest()) return;

      if (isRight(result)) {
        setPublishers(result.right.publishers);
        setPaginationInfo(
          pagination.currentPage,
          result.right.pagination.totalPages,
          result.right.pagination.total,
        );
      } else {
        toast.error(result.left.message);
      }
    } catch (error) {
      if (!isCurrentRequest()) return;

      captureAndReportError(error, {
        operation: 'publishersPage.loadPublishers',
        component: 'PublishersPage',
        filters,
        page: pagination.currentPage,
      });
      toast.error('Failed to load publishers');
    }
  };

  const { filters, sorting, pagination, isLoading, reload } = useManagementCrud(
    {
      enabled: canRender && activeView === 'publishers',
      store: publishersPageStore,
      actions: {
        updateFilter,
        setSorting,
        setPage,
        setPaginationInfo,
        setLoading,
      },
      loadPage: loadPublishers,
    },
  );

  useUqlUrlSync({
    enabled: canRender && activeView === 'publishers',
    parse: parsePublishersUql,
    build: buildPublishersUql,
    apply: setPublishersQueryState,
    current: { filters, sorting, page: pagination.currentPage },
  });

  const handleExportFieldToggle = (
    fieldKey: PublisherExportFieldKey,
    checked: boolean,
  ) => {
    setSelectedExportFields((prev) => {
      if (checked) {
        return prev.includes(fieldKey) ? prev : [...prev, fieldKey];
      }
      return prev.length === 1 ? prev : prev.filter((key) => key !== fieldKey);
    });
  };

  const handleExportPublishersCsv = async () => {
    if (!trpc?.client || isExportingPublishers) return;

    try {
      setIsExportingPublishers(true);

      const requestFilters = {
        type: filters.type,
        kybStatus: filters.kybStatus,
        hasDapps: filters.hasDapps,
        ownersOnly: filters.ownersOnly ? true : undefined,
        search: filters.search || undefined,
      };
      const apiSorting = getPublisherApiSorting(sorting);
      const exportedPublishers: PublisherDetail[] = [];
      let page = 1;
      let totalPages = 1;

      do {
        const result = await trpc.client.listAllPublishers.query({
          filters: requestFilters,
          pagination: { page, limit: 100 },
          sorting: apiSorting,
        });

        if (!isRight(result)) {
          toast.error(result.left.message);
          return;
        }

        exportedPublishers.push(...result.right.publishers);
        totalPages = result.right.pagination.totalPages;
        page += 1;
      } while (page <= totalPages);

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      downloadFile(
        buildPublisherCsv(exportedPublishers, selectedExportFields),
        `publishers-${timestamp}.csv`,
        'text/csv',
      );
      toast.success(
        `Exported ${exportedPublishers.length.toLocaleString()} ${
          exportedPublishers.length === 1 ? 'publisher' : 'publishers'
        }`,
      );
    } catch (error) {
      captureAndReportError(error, {
        operation: 'publishersPage.exportPublishersCsv',
        component: 'PublishersPage',
        filters,
      });
      toast.error('Failed to export publishers');
    } finally {
      setIsExportingPublishers(false);
    }
  };

  const handleKycBypassToggle = async (publisher: PublisherDetail) => {
    if (!trpc?.client) return;

    const publisherKey = getPublisherEntityKey(publisher);
    const nextState = !publisher.kycKybBypassEnabled;
    setUpdatingKycBypassPublisherKeys((prev) =>
      new Set(prev).add(publisherKey),
    );

    try {
      const result = await trpc.client.updateSuperPublisherKycKybBypass.mutate({
        publisherUserJoinId: publisher.id,
        kycKybBypassEnabled: nextState,
      });

      if (result._tag === 'Left') {
        throw new Error(result.left.message);
      }

      updatePublisherKycKybBypassState(
        publisher,
        result.right.kycKybBypassEnabled,
      );
      toast.success(result.right.message);
    } catch (error) {
      captureAndReportError(error, {
        operation: 'publishersPage.updateKycKybBypass',
        component: 'PublishersPage',
        publisherUserJoinId: publisher.id,
      });
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update publisher KYC/KYB bypass state',
      );
    } finally {
      setUpdatingKycBypassPublisherKeys((prev) => {
        const next = new Set(prev);
        next.delete(publisherKey);
        return next;
      });
    }
  };

  if (!canRender) return null;

  const isUpdatingBypass = (publisher: PublisherDetail) =>
    updatingKycBypassPublisherKeys.has(getPublisherEntityKey(publisher));
  const activeFilterCount = [
    filters.type !== 'all',
    filters.kybStatus !== 'all',
    filters.hasDapps !== 'all',
    filters.ownersOnly,
  ].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0 || filters.search !== '';
  const isEmpty = !isLoading && publishers.length === 0;
  const openPublisher = (publisher: PublisherDetail) =>
    navigate(`/publishers/${publisher.id}`);

  const columns = [
    listColumn<PublisherDetail>(
      'name',
      'Publisher',
      300,
      { sortable: true },
      (publisher) => <PublisherIdentity publisher={publisher} />,
    ),
    listColumn<PublisherDetail>(
      'type',
      'Type',
      150,
      { sortable: true },
      (publisher) => (
        <div className="flex items-center gap-1.5 text-xs text-neutral-300">
          {publisher.type === 'Organization' ? (
            <BusinessIcon className="size-3.5 fill-current text-neutral-500" />
          ) : (
            <PersonIcon className="size-3.5 fill-current text-neutral-500" />
          )}
          <span>{publisher.type}</span>
          {canManagePublishers && publisher.isOwner && <OwnerChip />}
        </div>
      ),
    ),
    listColumn<PublisherDetail>(
      'kybStatus',
      'KYB',
      150,
      { sortable: true },
      (publisher) =>
        publisher.kybStatus ? (
          <TypedStatusBadge type="kyb" status={publisher.kybStatus} />
        ) : (
          <span className="text-xs text-neutral-500">Not started</span>
        ),
    ),
    ...(canManagePublishers
      ? [
          listColumn<PublisherDetail>(
            'kycKybBypassEnabled',
            'Bypass',
            90,
            {},
            (publisher) => (
              <KycKybBypassSwitch
                publisher={publisher}
                isUpdating={isUpdatingBypass(publisher)}
                onToggle={handleKycBypassToggle}
              />
            ),
          ),
        ]
      : []),
    listColumn<PublisherDetail>(
      'dappCount',
      'Dapps',
      130,
      { sortable: true, descFirst: true },
      (publisher) => (
        <div className="flex items-baseline gap-1.5">
          <span className="text-sm tabular-nums text-neutral-200">
            {publisher.dappCount}
          </span>
          {publisher.publishedDappCount > 0 && (
            <span className="text-xs tabular-nums text-teal-400">
              {publisher.publishedDappCount} live
            </span>
          )}
        </div>
      ),
    ),
    listColumn<PublisherDetail>('members', 'Members', 100, {}, (publisher) => (
      <span className="text-sm tabular-nums text-neutral-200">
        {publisher.memberCount}
      </span>
    )),
    ...(canManagePublishers
      ? [
          listColumn<PublisherDetail>(
            'owner',
            'Account',
            240,
            {},
            (publisher) => (
              <div className="min-w-0">
                <div className="truncate text-sm text-neutral-200">
                  {publisher.user.fullName || publisher.user.email}
                </div>
                {publisher.user.fullName && (
                  <div className="truncate text-xs text-neutral-500">
                    {publisher.user.email}
                  </div>
                )}
              </div>
            ),
          ),
        ]
      : []),
    listColumn<PublisherDetail>(
      'created',
      'Joined',
      110,
      { sortable: true, descFirst: true },
      (publisher) => (
        <RelativeTime
          timestamp={publisher.created}
          className="text-xs text-neutral-300"
        />
      ),
    ),
  ];

  const exportActions = canManagePublishers ? (
    <>
      <PublisherExportFieldMenu
        selectedFields={selectedExportFields}
        onToggleField={handleExportFieldToggle}
      />
      <button
        type="button"
        disabled={isExportingPublishers}
        onClick={handleExportPublishersCsv}
        className={cn(
          toolbarControlClassName,
          'h-8 text-xs disabled:cursor-not-allowed disabled:opacity-60',
        )}
      >
        <CloudDownloadIcon className="size-4 fill-current text-neutral-400" />
        {isExportingPublishers ? 'Exporting…' : 'Export CSV'}
      </button>
    </>
  ) : null;

  return (
    <ManagementPageLayout
      authState={authState}
      canAccess={canAccessPublisherPages}
      className="flex-1 overflow-y-auto p-4 md:p-6"
    >
      <div className="space-y-4">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="hidden size-10 shrink-0 items-center justify-center rounded-xl bg-neutral-800 sm:flex">
              <GroupIcon className="size-5 fill-current text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-semibold tracking-tight text-white sm:text-2xl">
                Publishers
              </h1>
              <p className="text-sm text-neutral-400">
                Every publisher account, with its verification standing.
              </p>
            </div>
          </div>
          {canAccessPublisherReplies && (
            <SegmentedControl
              label="Publisher views"
              value={activeView}
              options={VIEW_OPTIONS}
              onChange={updateActiveView}
              className="text-sm"
            />
          )}
        </header>

        {activeView === 'publisher-replies' ? (
          <PublisherReviewRepliesView />
        ) : (
          <>
            <ListToolbar
              compact={isNarrow}
              activeFilterCount={activeFilterCount}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={resetFilters}
              search={
                <ToolbarSearch
                  label="Search publishers"
                  placeholder={
                    canManagePublishers
                      ? 'Search name, email, website…'
                      : 'Search name, website…'
                  }
                  value={filters.search}
                  onChange={(value) => updateFilter('search', value)}
                />
              }
              filters={
                <>
                  <ToolbarSelect
                    label="KYB status"
                    value={filters.kybStatus}
                    options={KYB_OPTIONS}
                    onChange={(value) => updateFilter('kybStatus', value)}
                  />
                  <ToolbarSelect
                    label="Publisher type"
                    value={filters.type}
                    options={TYPE_OPTIONS}
                    onChange={(value) => updateFilter('type', value)}
                  />
                  <ToolbarSelect
                    label="Dapps"
                    value={filters.hasDapps}
                    options={DAPPS_OPTIONS}
                    onChange={(value) => updateFilter('hasDapps', value)}
                  />
                  {canManagePublishers && (
                    <ToolbarSelect
                      label="Membership"
                      value={filters.ownersOnly ? 'true' : 'all'}
                      options={OWNERS_OPTIONS}
                      onChange={(value) =>
                        updateFilter('ownersOnly', value === 'true')
                      }
                    />
                  )}
                </>
              }
              trailing={
                <SortMenu
                  label="Sort publishers"
                  options={SORT_OPTIONS}
                  sorting={sorting}
                  onSortingChange={setSorting}
                />
              }
            />

            <ListShell>
              <ListHeader
                total={pagination.total}
                noun={['publisher', 'publishers']}
                isLoading={isLoading}
                onReload={() => {
                  void reload();
                }}
                actions={exportActions}
              />
              {isEmpty ? (
                <ListEmptyState
                  icon={GroupIcon}
                  title="No publishers yet"
                  hint="Accounts appear here once someone creates a publisher profile."
                  hasActiveFilters={hasActiveFilters}
                  onClearFilters={resetFilters}
                />
              ) : isNarrow ? (
                <RowList
                  items={publishers}
                  getId={(publisher) => publisher.id}
                  isLoading={isLoading}
                  onSelect={openPublisher}
                  renderRow={(publisher) => (
                    <PublisherRow
                      publisher={publisher}
                      canManagePublishers={canManagePublishers}
                    />
                  )}
                  renderTrailing={
                    canManagePublishers
                      ? (publisher) => (
                          <KycKybBypassSwitch
                            publisher={publisher}
                            isUpdating={isUpdatingBypass(publisher)}
                            onToggle={handleKycBypassToggle}
                          />
                        )
                      : undefined
                  }
                />
              ) : (
                <DataTable
                  data={publishers}
                  columns={columns}
                  isLoading={isLoading}
                  onRowClick={openPublisher}
                  getRowId={(row) => row.id}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  enableSorting
                  headerRowClassName="bg-neutral-900/80"
                />
              )}
              <ListPager
                pagination={pagination}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            </ListShell>
          </>
        )}
      </div>
    </ManagementPageLayout>
  );
}

function PublisherExportFieldMenu({
  selectedFields,
  onToggleField,
}: {
  selectedFields: PublisherExportFieldKey[];
  onToggleField: (fieldKey: PublisherExportFieldKey, checked: boolean) => void;
}) {
  const selectedFieldSet = new Set(selectedFields);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(toolbarControlClassName, 'h-8 text-xs')}
          aria-label="CSV fields"
        >
          Fields ({selectedFields.length})
          <KeyboardArrowDownIcon className="size-4 fill-current text-neutral-500" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="max-h-80 w-64 overflow-y-auto rounded-lg border border-neutral-700 bg-neutral-900 p-2 text-white"
      >
        <DropdownMenuLabel className="px-2 py-1 text-xs uppercase tracking-wide text-neutral-500">
          CSV fields
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-neutral-700" />
        {publisherExportFields.map((field) => (
          <DropdownMenuCheckboxItem
            key={field.key}
            checked={selectedFieldSet.has(field.key)}
            disabled={
              selectedFields.length === 1 && selectedFieldSet.has(field.key)
            }
            onSelect={(event) => event.preventDefault()}
            onCheckedChange={(checked) =>
              onToggleField(field.key, Boolean(checked))
            }
            className="cursor-pointer rounded text-sm text-neutral-200 focus:bg-neutral-800 focus:text-white data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40"
          >
            {field.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function getPublisherEntityKey(publisher: PublisherDetail) {
  if (publisher.organizationId) {
    return `organization:${publisher.organizationId}`;
  }
  if (publisher.individualId) {
    return `individual:${publisher.individualId}`;
  }
  return `join:${publisher.id}`;
}

function KycKybBypassSwitch({
  publisher,
  isUpdating,
  onToggle,
}: {
  publisher: PublisherDetail;
  isUpdating: boolean;
  onToggle: (publisher: PublisherDetail) => void;
}) {
  const isEnabled = publisher.kycKybBypassEnabled;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isEnabled}
      aria-label={`${isEnabled ? 'Disable' : 'Enable'} KYC/B bypass for ${publisher.name}`}
      title={isEnabled ? 'KYC/B bypass enabled' : 'KYC/B bypass disabled'}
      disabled={isUpdating}
      onClick={(event) => {
        event.stopPropagation();
        onToggle(publisher);
      }}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 disabled:cursor-not-allowed disabled:opacity-60',
        isEnabled
          ? 'border-amber-400/60 bg-amber-500/70'
          : 'border-neutral-600 bg-neutral-700',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-block size-4 rounded-full bg-white shadow-sm transition-transform',
          isEnabled ? 'translate-x-4' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

function OwnerChip() {
  return (
    <span className="inline-flex items-center rounded-full border border-teal-500/30 bg-teal-500/10 px-1.5 py-0.5 text-[10px] font-medium text-teal-400">
      Owner
    </span>
  );
}

function FrozenChip() {
  return (
    <span className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-1.5 py-0.5 text-[10px] font-medium text-red-300">
      Frozen
    </span>
  );
}

function PublisherAvatar({
  publisher,
  className,
}: {
  publisher: PublisherDetail;
  className?: string;
}) {
  const Icon = publisher.type === 'Organization' ? BusinessIcon : PersonIcon;
  const fallback = (
    <div
      className={cn(
        'flex size-10 shrink-0 items-center justify-center rounded-lg border border-neutral-700/50 bg-neutral-800',
        className,
      )}
    >
      <Icon className="size-5 fill-current text-neutral-500" />
    </div>
  );

  if (
    publisher.type !== 'Individual' ||
    !(publisher.avatarUrl || publisher.avatarThumbnailUrl)
  ) {
    return fallback;
  }

  return (
    <S3Image
      src={publisher.avatarUrl}
      thumbnailSrc={publisher.avatarThumbnailUrl}
      alt=""
      aria-hidden="true"
      className={cn(
        'size-10 shrink-0 rounded-lg border border-neutral-700/50 object-cover',
        className,
      )}
      fallback={fallback}
    />
  );
}

function PublisherIdentity({ publisher }: { publisher: PublisherDetail }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <PublisherAvatar publisher={publisher} />
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-white">
            {publisher.name}
          </span>
          {publisher.isFrozen && <FrozenChip />}
        </div>
        <div className="truncate text-xs text-neutral-400">
          {publisher.website || 'No website'}
        </div>
      </div>
    </div>
  );
}

/** Phone row: identity and KYB, then type, dapps and members. */
function PublisherRow({
  publisher,
  canManagePublishers,
}: {
  publisher: PublisherDetail;
  canManagePublishers: boolean;
}) {
  return (
    <>
      <PublisherAvatar publisher={publisher} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-white">
              {publisher.name}
            </span>
            {publisher.isFrozen && <FrozenChip />}
          </span>
          <RelativeTime
            timestamp={publisher.created}
            className="shrink-0 text-xs text-neutral-500"
          />
        </div>
        <div className="mt-0.5 truncate text-xs text-neutral-400">
          {canManagePublishers
            ? publisher.user.email
            : publisher.website || 'No website'}
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            {publisher.kybStatus ? (
              <TypedStatusBadge type="kyb" status={publisher.kybStatus} />
            ) : (
              <span className="text-xs text-neutral-500">KYB not started</span>
            )}
            {canManagePublishers && publisher.isOwner && <OwnerChip />}
          </div>
          <span className="shrink-0 text-xs tabular-nums text-neutral-500">
            {publisher.type} · {publisher.dappCount}{' '}
            {publisher.dappCount === 1 ? 'dapp' : 'dapps'} ·{' '}
            {publisher.memberCount}{' '}
            {publisher.memberCount === 1 ? 'member' : 'members'}
          </span>
        </div>
      </div>
    </>
  );
}
